/**
 * UserProfileCard – نمایش پروفایل کاربر؛ هیروی تمام‌عرض + بخش‌های اطلاعات
 */

import Loading from "@/common/components/atom/loading";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "../router";
import { usePresence } from "../context/PresenceContext";
import { getUserProfile } from "../api/profile";
import { pazireshProfileAvatarUrl } from "../utils/pazireshAvatarUrl";
import { useQuickChatUserOpener } from "../hooks/useQuickChatUserOpener";
import { Link2, ChevronDown } from "lucide-react";
import BackIcon from "@/common/components/icons/back";
import ProfileHeroShell, { GLASS_BASE, GLASS_ROUND, ProfileHeroAction, ProfileHeroActionLink, ProfileHeroGlassButton } from "./ProfileHeroShell";
import ProfileShareButton from "./ProfileShareButton";
import QuickChatIcon from "./QuickChatIcon";
import QuickChatRecentChoiceModal from "./QuickChatRecentChoiceModal";
import PillTabs from "./PillTabs";
import UserProfileChatsList from "./UserProfileChatsList";
import HealthRecordProfileSection from "./healthRecord/HealthRecordProfileSection";
import { toUserMessage } from "../utils/userMessage";
import { toPersianDigits } from "../utils/persianDate";
import { usePageBack } from "../utils/modals";
import classNames from "@/common/utils/classNames";
import { LoadFailed, RowsSkeleton } from "@/modules/doctorHome/components/profileSettings/chatSettingsUi";

export default function UserProfileCard({
  userId,
  onClose,
  userDisplayName: userDisplayNameProp,
  avatarUrl: avatarUrlProp,
  currentUserRemoteId,
  currentTicketId = null,
  layout = "modal",
  extraRef = null,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [userDisplayName, setUserDisplayName] = useState(userDisplayNameProp ?? "کاربر");
  const [longExpanded, setLongExpanded] = useState(false);
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [sharedTickets, setSharedTickets] = useState([]);
  const [attempt, setAttempt] = useState(0);
  // null until the profile is in; a part that fills later adds its tab but never moves the person off this one
  const [tab, setTab] = useState(null);
  // the opener's part (the chat's appointment) arrives through a portal into this box
  const [extraBox, setExtraBox] = useState(null);
  const [extraFilled, setExtraFilled] = useState(false);
  const setExtraRef = useCallback(
    (node) => {
      extraRef?.(node);
      setExtraBox(node);
    },
    [extraRef]
  );
  useEffect(() => {
    if (!extraBox) return undefined;
    const check = () => setExtraFilled(extraBox.childElementCount > 0);
    check();
    const observer = new MutationObserver(check);
    observer.observe(extraBox, { childList: true });
    return () => observer.disconnect();
  }, [extraBox]);

  const {
    quickChatChoice,
    setQuickChatChoice,
    quickChatCreating,
    quickChatBusyUserId,
    handleOpenQuickChatUser,
    handleQuickChatCreateAndOpen,
    handleContinueRecentChat,
  } = useQuickChatUserOpener({ onAfterNavigate: onClose });

  const goBack = usePageBack();
  const presence = usePresence();
  const isOnline = presence?.isOnline ?? (() => false);
  const isCurrentUser =
    currentUserRemoteId != null && userId != null && Number(userId) === Number(currentUserRemoteId);

  useEffect(() => {
    if (userDisplayNameProp != null) setUserDisplayName(userDisplayNameProp);
  }, [userDisplayNameProp]);

  useEffect(() => {
    if (userId == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setTab(null);
    getUserProfile(userId)
      .then((data) => {
        if (!cancelled) setProfile(data);
      })
      .catch((e) => {
        if (!cancelled) setError(toUserMessage(e?.message, "خطا در بارگذاری پروفایل"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, attempt]);

  const resolvedAvatarUrl = useMemo(() => {
    if (avatarUrlProp) return avatarUrlProp;
    return userId != null ? pazireshProfileAvatarUrl(userId) : null;
  }, [avatarUrlProp, userId]);

  if (userId == null) return null;

  const short = profile?.short_description ?? "";
  const long = profile?.long_description ?? "";
  const tags = Array.isArray(profile?.tags) ? profile.tags : [];
  const links = Array.isArray(profile?.links) ? profile.links : [];

  const shortText = short.trim();
  const longText = long.trim();
  const displayTags = tags.filter((tag) => {
    const value = String(tag || "").trim();
    return value && value !== shortText;
  });
  const primaryLink = links.find((link) => (link?.url || "").trim());
  const visibleTags = tagsExpanded ? displayTags : displayTags.slice(0, 6);
  const hasBio = Boolean(longText);
  const linkLabel = primaryLink?.label?.trim() || "مشاهده لینک";
  const quickChatEnabled = !!profile?.quick_chat_enabled;
  const messagingBlocked = !!profile?.block_status?.is_blocked;
  const isQuickChatLoading =
    quickChatBusyUserId != null && Number(quickChatBusyUserId) === Number(userId);
  const quickChatUser = useMemo(
    () => ({
      remote_id: userId,
      full_name: userDisplayName,
      name: userDisplayName,
    }),
    [userId, userDisplayName]
  );

  const heroSubtitle = loading
    ? "در حال بارگذاری..."
    : shortText || (isOnline(userId) ? "آنلاین" : "کاربر");

  const heroActions = [];
  // holds the quick-chat tile's place, so the share tile does not resize when the profile arrives
  if (loading && !isCurrentUser) {
    heroActions.push(<div key="quick-chat" className="h-[62px] min-w-0 flex-1 basis-0 rounded-xl bg-white" aria-hidden />);
  }
  if (!loading && !isCurrentUser && quickChatEnabled && !messagingBlocked) {
    heroActions.push(
      <ProfileHeroAction
        key="quick-chat"
        variant="primary"
        label={isQuickChatLoading ? "بررسی..." : "گفت‌وگوی سریع"}
        disabled={isQuickChatLoading || quickChatCreating}
        onClick={() => handleOpenQuickChatUser(quickChatUser, { tickets: sharedTickets })}
        icon={
          isQuickChatLoading ? (
            <Loading width={20} height={20} />
          ) : (
            <QuickChatIcon size={24} />
          )
        }
      />
    );
  }
  if (!loading && primaryLink) {
    heroActions.push(
      <ProfileHeroActionLink
        key="primary-link"
        href={primaryLink.url}
        label={linkLabel}
        title={linkLabel}
        icon={
          <Link2 size={24} strokeWidth={1.5} aria-hidden />
        }
      />
    );
  }
  heroActions.push(
    <ProfileShareButton
      key="share"
      variant="action"
      remoteId={userId}
      displayName={userDisplayName}
    />
  );

  const bodyTags =
    !loading && displayTags.length > 0 ? (
      <div className="flex flex-wrap gap-1.5 px-1 pb-1">
        {visibleTags.map((tag, index) => (
          <span key={`${tag}-${index}`} className="inline-flex items-center rounded-full bg-primary/10 px-3 py-[5px] text-[12px] font-medium leading-[1.2] text-primary">
            {tag}
          </span>
        ))}
        {!tagsExpanded && displayTags.length > 6 && (
          <button
            type="button"
            className="inline-flex cursor-pointer items-center rounded-full border border-dashed border-primary/30 bg-primary/5 px-3 py-[5px] text-[12px] font-medium leading-[1.2] text-primary active:bg-primary/10"
            onClick={() => setTagsExpanded(true)}
            aria-label={`مشاهده ${displayTags.length - 6} برچسب دیگر`}
          >
            +{displayTags.length - 6}
          </button>
        )}
      </div>
    ) : null;

  // a tab only for a part that has something in it
  const hasInfo = Boolean(error || bodyTags || hasBio || extraFilled || isCurrentUser);
  const tabs = [
    ...(hasInfo ? [{ id: "info", label: "اطلاعات" }] : []),
    ...(!isCurrentUser && !error
      ? [{ id: "chats", label: "گفت‌وگوها", badge: sharedTickets.length ? toPersianDigits(sharedTickets.length) : null }]
      : []),
  ];
  if (!loading && tab === null) setTab(hasInfo ? "info" : "chats");
  const activeTab = tabs.some((item) => item.id === tab) ? tab : tabs[0]?.id;
  // the opener's part can still add a tab: its row keeps its height, so nothing under it jumps
  const showTabs = !loading && tabs.length > 1;
  const reserveTabs = !showTabs && !isCurrentUser && !error && (loading || !!extraRef);

  return (
    <div
      className={classNames(
        "user-profile-card flex flex-col bg-[#F2F2F2]",
        `user-profile-card--${layout}`,
        layout === "page"
          ? "min-h-[var(--viewport-height,100dvh)]"
          : "min-h-0 w-full min-w-0 max-w-none overflow-y-auto overscroll-contain"
      )}
      role={layout === "page" ? undefined : "dialog"}
      aria-label="پروفایل کاربر"
      dir="rtl"
    >
      {/* contents: its sticky toolbar stays through the whole page's scroll, not only the hero's */}
      <ProfileHeroShell
        className="!contents"
        remoteId={userId}
        fullName={userDisplayName}
        avatarUrl={resolvedAvatarUrl}
        name={userDisplayName}
        subtitle={heroSubtitle}
        toolbarStart={
          layout === "page" ? (
            <ProfileHeroGlassButton className={GLASS_ROUND} onClick={goBack} aria-label="بازگشت">
              <BackIcon className="h-6 w-6" aria-hidden />
            </ProfileHeroGlassButton>
          ) : onClose ? (
            <ProfileHeroGlassButton className={GLASS_ROUND} onClick={onClose} aria-label="بازگشت">
              <BackIcon className="h-6 w-6" aria-hidden />
            </ProfileHeroGlassButton>
          ) : null
        }
        toolbarEnd={
          isCurrentUser && !loading ? (
            <Link to="/chat-profile" className={GLASS_BASE} onClick={onClose}>
              ویرایش
            </Link>
          ) : null
        }
        actions={heroActions.length > 0 ? heroActions : null}
      />

      {/* mounted while loading too: the opener's part and the chats settle before the first tab is chosen */}
      <div className="user-profile-card__body relative flex flex-1 flex-col gap-3 bg-[#F2F2F2] px-3 pb-[calc(20px+env(safe-area-inset-bottom))] pt-1">
        {(showTabs || reserveTabs) && (
          <div className="sticky top-[calc(3.5rem+env(safe-area-inset-top))] z-[2] -mx-3 -mt-1 bg-[#F2F2F2] px-3 py-2">
            {showTabs ? (
              <PillTabs className="!bg-[#E4E4E9]" label="بخش‌های پروفایل" value={activeTab} onChange={setTab} items={tabs} />
            ) : (
              <div className="h-[35px]" aria-hidden />
            )}
          </div>
        )}
        {loading && <RowsSkeleton rows={4} />}
          <div className={classNames("flex flex-col gap-3", (loading || activeTab !== "info") && "hidden")}>
          {error && <LoadFailed onRetry={() => setAttempt((n) => n + 1)} />}
          {bodyTags}

          {hasBio && (
            <section className="user-profile-card__bio-card rounded-[14px] bg-white px-[18px] pb-3 pt-3.5">
              <p
                className={classNames(
                  "user-profile-card__bio-text m-0 whitespace-pre-wrap text-[15px] leading-[26px] text-[#222]",
                  !isCurrentUser && !longExpanded && "user-profile-card__bio-text--clamped line-clamp-4"
                )}
              >
                {longText}
              </p>
              {!isCurrentUser && longText.length > 140 && (
                <button
                  type="button"
                  className={classNames(
                    "user-profile-card__bio-toggle mt-2 flex w-full cursor-pointer items-center justify-center border-none bg-transparent pb-0 pt-0.5 text-slate-400",
                    longExpanded && "user-profile-card__bio-toggle--expanded [&_svg]:rotate-180"
                  )}
                  onClick={() => setLongExpanded((value) => !value)}
                  aria-label={longExpanded ? "جمع‌کردن" : "ادامه مطلب"}
                >
                  <ChevronDown size={18} strokeWidth={2} aria-hidden />
                </button>
              )}
            </section>
          )}

          {extraRef && <div ref={setExtraRef} className="empty:hidden" />}

          {isCurrentUser && <HealthRecordProfileSection />}
          </div>

          {!isCurrentUser && !error && (
            <div className={classNames((loading || activeTab !== "chats") && "hidden")}>
              <UserProfileChatsList
                personRemoteId={userId}
                currentTicketId={currentTicketId}
                onClose={onClose}
                onTicketsLoaded={setSharedTickets}
              />
            </div>
          )}
      </div>

      <QuickChatRecentChoiceModal
        isOpen={!!quickChatChoice}
        user={quickChatChoice?.user}
        recentTicket={quickChatChoice?.recentTicket}
        creating={quickChatCreating}
        onClose={() => {
          if (!quickChatCreating) setQuickChatChoice(null);
        }}
        onContinue={handleContinueRecentChat}
        onCreateNew={() => {
          const remoteId = quickChatChoice?.user?.remote_id;
          if (remoteId != null) handleQuickChatCreateAndOpen(remoteId);
        }}
      />
    </div>
  );
}
